import React, { useCallback, useMemo, useState } from "react";
import { useTreeModifyContext } from "../TreeDataContext";
import { Alert, Box, Card, IconButton, ListItem, TextField, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import RenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import { parseIntWithinLimits } from "../Helpers";
import { Coord, CoordsData } from "./PositionsData";
import { usePathContext } from "../PathInfo";

interface PositionsEditorCoordCardProps {
  coordId: string | number;
  x: number;
  y: number;
  allCoords: CoordsData;
}

export function PositionsEditorCoordCard(props: PositionsEditorCoordCardProps) {
  const [isModifying, setIsModifying] = useState(false);
  const [newCoordId, setNewCoordId] = useState(props.coordId);
  const [newX, setNewX] = useState(props.x);
  const [newY, setNewY] = useState(props.y);
  const [currentErrors, setCurrentErrors] = useState<string[]>([]);

  const { path } = usePathContext();
  const { saveChangesAtPath, removeAtPath } = useTreeModifyContext();

  const canBeSaved = useMemo(() => {
    setCurrentErrors([]);
    const isCoordValueChanged = newX !== props.x || newY !== props.y;
    const isCoordIdChanged = newCoordId !== props.coordId;
    if (newCoordId.toString() === "") {
      return false;
    }
    if (!isCoordValueChanged && !isCoordIdChanged) {
      return false;
    }
    if (isCoordIdChanged) {
      if (!Array.isArray(props.allCoords) && Object.keys(props.allCoords).includes(newCoordId.toString())) {
        // Cannot save over a different named coord
        setCurrentErrors((prevErrors) => [...prevErrors, "Cannot save over a different named coordinate"]);
        return false;
      }
    }

    return true;
  }, [newX, props.x, props.y, props.coordId, props.allCoords, newY, newCoordId]);

  const saveChanges = useCallback(() => {
    const newCoord = { x: newX, y: newY };
    if (typeof props.coordId === "number" && Array.isArray(props.allCoords)) {
      removeAtPath(path);
      let newCoords = props.allCoords.slice() as Coord[];
      newCoords.splice(props.coordId, 1);
      newCoords.splice(parseIntWithinLimits(newCoordId.toString(), 0, newCoords.length), 0, newCoord);
      saveChangesAtPath(newCoords, path);
    } else if (typeof props.coordId === "string" && !Array.isArray(props.allCoords)) {
      removeAtPath(path);
      let newCoords = Object.assign({}, props.allCoords);
      delete newCoords[props.coordId];
      newCoords[newCoordId] = newCoord;
      saveChangesAtPath(newCoords, path);
    }
  }, [newX, newY, props.coordId, props.allCoords, removeAtPath, path, newCoordId, saveChangesAtPath]);

  const getSecondaryActions = useCallback(() => {
    if (isModifying) {
      if (canBeSaved) {
        return (
          <IconButton
            color="success"
            aria-label="save"
            onClick={() => {
              saveChanges();
              setIsModifying(false);
            }}
          >
            <CheckIcon />
          </IconButton>
        );
      }
      return (
        <IconButton color="error" aria-label="rename" onClick={() => setIsModifying(false)}>
          <CloseIcon />
        </IconButton>
      );
    }
    return (
      <Box className={"vertical-flex"}>
        <IconButton color="primary" aria-label="rename" onClick={() => setIsModifying(true)}>
          <RenameOutlineIcon />
        </IconButton>
        <IconButton
          color="error"
          aria-label="delete"
          onClick={() => removeAtPath(path.concat([props.coordId.toString()]))}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    );
  }, [isModifying, canBeSaved, saveChanges, removeAtPath, path, props.coordId]);

  if (isModifying) {
    return (
      <Card elevation={8} key={props.coordId} sx={{ width: "100%" }}>
        {currentErrors.map((error, index) => {
          return (
            <Alert severity="warning">
              <Typography>{error}</Typography>
            </Alert>
          );
        })}
        <ListItem secondaryAction={getSecondaryActions()}>
          <Box className="card-item">
            <TextField
              variant={"standard"}
              size="small"
              id="new-coord-id"
              label={"New coord " + (Array.isArray(props.allCoords) === true ? "index" : "name")}
              autoFocus={true}
              value={newCoordId}
              onChange={(event) => {
                if (Array.isArray(props.allCoords)) {
                  setNewCoordId(parseIntWithinLimits(event.target.value, 0, props.allCoords.length - 1));
                } else {
                  setNewCoordId(event.target.value);
                }
              }}
            ></TextField>
            <Box className="card-item-settings-2cols">
              <Typography>X: </Typography>
              <TextField
                variant={"standard"}
                sx={{ minWidth: "6rem" }}
                size="small"
                id="new-area-left"
                value={newX}
                onChange={(event) => {
                  setNewX(parseIntWithinLimits(event.target.value, -10000, 10000));
                }}
              ></TextField>
              <Typography>Y: </Typography>
              <TextField
                variant={"standard"}
                sx={{ minWidth: "6rem" }}
                size="small"
                id="new-area-top"
                value={newY}
                onChange={(event) => {
                  setNewY(parseIntWithinLimits(event.target.value, -10000, 10000));
                }}
              ></TextField>
            </Box>
          </Box>
        </ListItem>
      </Card>
    );
  }

  return (
    <Card elevation={5} key={props.coordId} sx={{ width: "100%" }}>
      <ListItem secondaryAction={getSecondaryActions()}>
        <Box className="card-item">
          <Typography variant="h6">{props.coordId}</Typography>
          <Box className="card-item-settings-2cols">
            <Typography>X: </Typography>
            <Typography>{props.x}</Typography>
            <Typography>Y: </Typography>
            <Typography>{props.y}</Typography>
          </Box>
        </Box>
      </ListItem>
    </Card>
  );
}
